import { useState, useEffect, useCallback } from 'react';
import googleSheets from '../services/googleSheets';
import { Customer } from '../types';

export interface CustomerWithMetrics extends Customer {
  progress: number;
  healthScore?: number;
  daysInOnboarding: number;
  lastUpdatedBy?: string;
  lastUpdatedDate?: string;
}

export interface DashboardStats {
  total: number;
  completed: number;
  atRisk: number;
  avgProgress: number;
}

export const useDashboardData = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<CustomerWithMetrics[]>([]);

  const loadDashboardData = useCallback(async (clearCache: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      if (clearCache) {
        googleSheets.clearAllCache();
      }

      const allCustomers = await googleSheets.getAllCustomers();
      
      const customersWithMetrics = await Promise.all(
        allCustomers.map(async (customer) => {
          try {
            const [tasks, progress] = await Promise.all([
              googleSheets.getOnboardingTasks(customer.segment),
              googleSheets.getCustomerProgress(customer.id),
            ]);
            
            // Get health score - if it doesn't exist or is stale, it will be recalculated
            let healthScore = await googleSheets.getHealthScore(customer.id);
            
            // If no health score exists or it's older than 1 hour, recalculate it
            if (!healthScore || 
                (Date.now() - new Date(healthScore.calculatedDate).getTime()) > 60 * 60 * 1000) {
              try {
                healthScore = await googleSheets.calculateAndSaveHealthScore(customer.id);
              } catch (err) {
                console.warn(`Failed to calculate health score for ${customer.id}:`, err);
                healthScore = null;
              }
            }

            const requiredTasks = tasks.filter(t => t.taskType === 'required');
            const completedRequired = progress.filter(
              p => p.status === 'completed' && 
              requiredTasks.some(t => t.id === p.taskId)
            ).length;
            
            const progressPercentage = requiredTasks.length > 0 
              ? (completedRequired / requiredTasks.length) * 100 
              : 0;

            let daysInOnboarding = 0;
            try {
              if (customer.signupDate) {
                // For completed customers with completion date, use actual completion time
                if (customer.onboardingStatus === 'completed' && customer.onboardingCompletedDate) {
                  daysInOnboarding = Math.floor(
                    (new Date(customer.onboardingCompletedDate).getTime() - new Date(customer.signupDate).getTime()) / 
                    (1000 * 60 * 60 * 24)
                  );
                } else {
                  // For in-progress or customers without completion date, use current time
                  daysInOnboarding = Math.floor(
                    (new Date().getTime() - new Date(customer.signupDate).getTime()) / 
                    (1000 * 60 * 60 * 24)
                  );
                }
              }
            } catch {
              daysInOnboarding = 0;
            }

            let recentActivity = progress
              .filter(p => p.updatedByEmail && p.completedDate)
              .sort((a, b) => new Date(b.completedDate || '').getTime() - new Date(a.completedDate || '').getTime())[0];
            
            if (!recentActivity) {
              recentActivity = progress
                .filter(p => p.status === 'completed' && p.completedDate)
                .sort((a, b) => new Date(b.completedDate || '').getTime() - new Date(a.completedDate || '').getTime())[0];
            }

            const validProgressPercentage = isNaN(progressPercentage) ? 0 : Math.max(0, Math.min(100, progressPercentage));
            const validHealthScore = (healthScore?.score && !isNaN(healthScore.score)) ? healthScore.score : 0;
            const validDaysInOnboarding = isNaN(daysInOnboarding) ? 0 : Math.max(0, daysInOnboarding);

            // Validate and auto-correct status inconsistencies
            const expectedStatus = validProgressPercentage === 100 ? 'completed' : 
                                  validProgressPercentage > 0 ? 'in_progress' : 'not_started';
            
            let correctedCustomer = customer;
            if (customer.onboardingStatus !== expectedStatus) {
              console.warn(`Status inconsistency detected for ${customer.companyName}: status=${customer.onboardingStatus}, progress=${validProgressPercentage}%. Auto-correcting to ${expectedStatus}.`);
              
              // Auto-correct the status
              try {
                await googleSheets.updateCustomerStatus(customer.id, expectedStatus);
                correctedCustomer = { ...customer, onboardingStatus: expectedStatus };
              } catch (statusUpdateError) {
                console.error(`Failed to auto-correct status for ${customer.id}:`, statusUpdateError);
                // Continue with original customer data if update fails
              }
            }

            return {
              ...correctedCustomer,
              progress: validProgressPercentage,
              healthScore: validHealthScore,
              daysInOnboarding: validDaysInOnboarding,
              lastUpdatedBy: recentActivity?.updatedByName || (recentActivity ? 'System' : ''),
              lastUpdatedDate: recentActivity?.completedDate || '',
            };
          } catch (err) {
            return {
              id: customer?.id || 'unknown',
              email: customer?.email || 'unknown@example.com',
              companyName: customer?.companyName || 'Unknown Company',
              segment: customer?.segment || 'small_business',
              signupDate: customer?.signupDate || new Date().toISOString(),
              onboardingStatus: customer?.onboardingStatus || 'not_started',
              progress: 0,
              healthScore: 0,
              daysInOnboarding: 0,
              lastUpdatedBy: '',
              lastUpdatedDate: '',
            };
          }
        })
      );

      setCustomers(customersWithMetrics);
    } catch (err) {
      console.error('Dashboard data loading error:', err);
      
      // Check for specific error types
      if (err instanceof Error) {
        console.error('Error message:', err.message);
        console.error('Error stack:', err.stack);
        
        if (err.message === 'AUTHENTICATION_EXPIRED') {
          setError('Your session has expired. Please sign in again.');
        } else if (err.message.includes('No valid authentication token')) {
          setError('Authentication required. Please sign in.');
        } else if (err.message.includes('Failed to fetch')) {
          setError('Network error. Please check your connection and try again.');
        } else {
          setError(`Failed to load dashboard data: ${err.message}`);
        }
      } else {
        setError('Failed to load dashboard data. Please check the console for details.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
    
    const interval = setInterval(() => {
      loadDashboardData(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [loadDashboardData]);

  const calculateStats = useCallback((): DashboardStats => {
    const total = customers.length;
    const completed = customers.filter(c => c.onboardingStatus === 'completed').length;
    const atRisk = customers.filter(c => {
      const score = c.healthScore || 0;
      return !isNaN(score) && score < 60;
    }).length;
    
    const validProgresses = customers
      .map(c => c.progress || 0)
      .filter(p => !isNaN(p));
    
    const avgProgress = validProgresses.length > 0 
      ? validProgresses.reduce((sum, p) => sum + p, 0) / validProgresses.length 
      : 0;

    return {
      total,
      completed,
      atRisk,
      avgProgress: Math.round(isNaN(avgProgress) ? 0 : avgProgress),
    };
  }, [customers]);

  return {
    loading,
    error,
    customers,
    setCustomers,
    loadDashboardData,
    calculateStats,
  };
};